import type { ApiSchema } from "vue-utils"

export class Month {
	/** Human readable, English, full name */
	readonly name: string

	/** Human readable, English, 3 letter name */
	readonly shortName: string

	/** 1 indexed, i.e. Jan=1, Dec=12 */
	readonly monthValue: number

	/** Maximum days in the month */
	readonly maximumDays: number

	private constructor(name: string, monthValue: number, maximumDays: number) {
		this.name = name
		this.shortName = name.substring(0, 3)
		this.monthValue = monthValue
		this.maximumDays = maximumDays
	}

	toJSON(): number {
		return this.monthValue
	}

	static readonly January = new Month("January", 1, 31)
	static readonly February = new Month("February", 2, 29)
	static readonly March = new Month("March", 3, 31)
	static readonly April = new Month("April", 4, 30)
	static readonly May = new Month("May", 5, 31)
	static readonly June = new Month("June", 6, 30)
	static readonly July = new Month("July", 7, 31)
	static readonly August = new Month("August", 8, 31)
	static readonly September = new Month("September", 9, 30)
	static readonly October = new Month("October", 10, 31)
	static readonly November = new Month("November", 11, 30)
	static readonly December = new Month("December", 12, 31)

	/**
	 * @param monthValue 1 indexed, i.e. 1 -> Jan, 12 -> Dec
	 * @throws {Error} If invalid monthValue
	 */
	static of(monthValue: number): Month {
		for (const month of Month) {
			if (month.monthValue === monthValue) {
				return month
			}
		}
		throw new Error(`Invalid month value ${monthValue}`)
	}

	static *[Symbol.iterator](): Iterator<Month> {
		yield Month.January
		yield Month.February
		yield Month.March
		yield Month.April
		yield Month.May
		yield Month.June
		yield Month.July
		yield Month.August
		yield Month.September
		yield Month.October
		yield Month.November
		yield Month.December
	}

	static values(): Month[] {
		return Array.from(Month)
	}

	static readonly Schema: ApiSchema<Month> = (month) => Month.of(month)
}
